import React from 'react';
import { Eye, EyeOff, Navigation } from 'lucide-react';
import { directionsUrl, embedUrl } from '../../utils/maps';

/* ─── Button ─── */
export function Button({
  children, variant = 'primary', size = 'md',
  loading, fullWidth, style: extra, ...props
}) {
  const [hovered, setHovered] = React.useState(false);

  // A parent can disable the button directly (<Button disabled={!valid}>),
  // not only through `loading`. That case used to render at full opacity with
  // a pointer cursor, so a button that ignored clicks looked completely live.
  const isDisabled = Boolean(loading || props.disabled);

  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px', fontWeight: 600, borderRadius: '8px',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s, border-color 0.15s',
    border: 'none', width: fullWidth ? '100%' : undefined,
    letterSpacing: '-0.01em',
    fontSize: size === 'sm' ? '12.5px' : size === 'lg' ? '15px' : '13.5px',
    padding: size === 'sm' ? '7px 14px' : size === 'lg' ? '13px 28px' : '10px 20px',
    opacity: isDisabled ? 0.65 : 1,
    ...extra,
  };

  const lift = hovered && !isDisabled;
  const variants = {
    primary: {
      background: lift ? 'var(--red-dark)' : 'var(--red)',
      color: 'white',
      boxShadow: lift ? 'var(--shadow-red)' : 'none',
      transform: lift ? 'translateY(-1px)' : 'none',
    },
    secondary: {
      background: lift ? 'var(--gray-200)' : 'var(--gray-100)',
      color: 'var(--gray-800)',
      transform: lift ? 'translateY(-1px)' : 'none',
    },
    outline: {
      background: lift ? 'var(--red-light)' : 'transparent',
      color: 'var(--red)',
      border: '1.5px solid var(--red)',
      transform: lift ? 'translateY(-1px)' : 'none',
    },
    ghost: {
      background: lift ? 'var(--gray-100)' : 'transparent',
      color: 'var(--gray-600)',
    },
    danger: {
      background: lift ? '#B91C1C' : '#DC2626',
      color: 'white',
      boxShadow: lift ? '0 4px 14px rgba(220,38,38,0.3)' : 'none',
      transform: lift ? 'translateY(-1px)' : 'none',
    },
  };

  return (
    <button
      style={{ ...base, ...variants[variant] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Tells the global stylesheet to keep its hands off :hover — this
      // component already darkens its own background per variant, and the
      // brightness filter there would stack on top and double-darken it.
      data-managed-hover=""
      {...props}
      disabled={isDisabled}
    >
      {loading && <Spinner size={14} />}
      {children}
    </button>
  );
}

/* ─── Card ─── */
export function Card({ children, style: extra, interactive, onClick }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => interactive && setHovered(true)}
      onMouseLeave={() => interactive && setHovered(false)}
      style={{
        background: 'var(--white)',
        border: '1px solid var(--gray-200)',
        borderRadius: 'var(--radius)',
        padding: '20px 22px',
        boxShadow: hovered && interactive ? 'var(--shadow-md)' : 'var(--shadow-sm)',
        transform: hovered && interactive ? 'translateY(-2px)' : 'none',
        transition: 'box-shadow var(--t), transform var(--t)',
        cursor: interactive ? 'pointer' : undefined,
        ...extra,
      }}
    >
      {children}
    </div>
  );
}

/* ─── Alert ─── */
export function Alert({ children, variant = 'error', style: extra }) {
  const map = {
    error:   { bg: 'var(--red-light)',    border: 'var(--red-mid)',  color: 'var(--red-dark)' },
    success: { bg: 'var(--green-light)',  border: '#A7DFB9',         color: 'var(--green-dark)' },
    warning: { bg: 'var(--amber-light)',  border: '#FDD07A',         color: 'var(--amber-dark)' },
    info:    { bg: 'var(--blue-light)',   border: '#BFDBFE',         color: 'var(--blue-dark)' },
  };
  const c = map[variant] || map.error;
  return (
    <div style={{
      padding: '11px 14px', borderRadius: 'var(--radius)',
      background: c.bg, border: `1px solid ${c.border}`,
      color: c.color, fontSize: '13px', fontWeight: 500,
      lineHeight: 1.5, marginBottom: '14px', ...extra,
    }}>
      {children}
    </div>
  );
}

/* ─── Badge ─── */
export function Badge({ children, color = 'gray' }) {
  const map = {
    red:    { bg: 'var(--red-light)',    text: 'var(--red-dark)' },
    green:  { bg: 'var(--green-light)',  text: 'var(--green-dark)' },
    yellow: { bg: 'var(--amber-light)',  text: 'var(--amber-dark)' },
    blue:   { bg: 'var(--blue-light)',   text: 'var(--blue-dark)' },
    gray:   { bg: 'var(--gray-100)',     text: 'var(--gray-600)' },
  };
  const c = map[color] || map.gray;
  return (
    <span style={{
      display: 'inline-block', fontSize: '11.5px', fontWeight: 600,
      padding: '3px 10px', borderRadius: '20px',
      background: c.bg, color: c.text, letterSpacing: '0.01em',
    }}>
      {children}
    </span>
  );
}

/* ─── Input ─── */
export function Input({ label, error, style: extra, type, ...props }) {
  const [focused, setFocused] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const isPassword = type === 'password';

  return (
    <div style={{ marginBottom: '16px' }}>
      {label && (
        <label style={{
          display: 'block', fontSize: '13.5px', fontWeight: 600,
          marginBottom: '6px', color: 'var(--gray-700)',
        }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          type={isPassword ? (visible ? 'text' : 'password') : type}
          style={{
            width: '100%', padding: '11px 14px',
            paddingRight: isPassword ? '42px' : '14px',
            border: `1.5px solid ${error ? 'var(--red)' : focused ? 'var(--red)' : 'var(--gray-200)'}`,
            borderRadius: '8px', outline: 'none',
            background: 'var(--white)',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            boxShadow: focused
              ? '0 0 0 3px rgba(200,16,46,0.12)'
              : error ? '0 0 0 3px rgba(200,16,46,0.08)' : 'none',
            fontSize: '14px', color: 'var(--gray-900)',
            ...extra,
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setVisible(v => !v)}
            tabIndex={-1}
            aria-label={visible ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
              display: 'flex', alignItems: 'center', color: 'var(--gray-400)',
            }}
          >
            {visible ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
      {error && <p style={{ fontSize: '12.5px', color: 'var(--red)', marginTop: '5px', fontWeight: 500 }}>{error}</p>}
    </div>
  );
}

/* ─── Select ─── */
export function Select({ label, children, style: extra, ...props }) {
  const [focused, setFocused] = React.useState(false);
  return (
    <div style={{ marginBottom: '16px' }}>
      {label && (
        <label style={{
          display: 'block', fontSize: '13.5px', fontWeight: 600,
          marginBottom: '6px', color: 'var(--gray-700)',
        }}>
          {label}
        </label>
      )}
      <select
        style={{
          width: '100%', padding: '11px 14px',
          border: `1.5px solid ${focused ? 'var(--red)' : 'var(--gray-200)'}`,
          borderRadius: '8px', fontSize: '14px',
          background: 'var(--white)', outline: 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          boxShadow: focused ? '0 0 0 3px rgba(200,16,46,0.12)' : 'none',
          color: 'var(--gray-900)',
          ...extra,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        {...props}
      >
        {children}
      </select>
    </div>
  );
}

/* ─── Spinner ─── */
export function Spinner({ size = 20 }) {
  return (
    <div style={{
      width: size, height: size,
      border: '2px solid var(--gray-200)',
      borderTopColor: 'var(--red)',
      borderRadius: '50%',
      animation: 'bb-spin 0.7s linear infinite',
      flexShrink: 0,
    }} />
  );
}

/* ─── Divider ─── */
export function Divider({ label, style: extra }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '6px 0', ...extra }}>
      <div style={{ flex: 1, height: '1px', background: 'var(--gray-200)' }} />
      {label && <span style={{ fontSize: '12px', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</span>}
      {label && <div style={{ flex: 1, height: '1px', background: 'var(--gray-200)' }} />}
    </div>
  );
}

/* ─── Domain badges ─── */
export function UrgencyBadge({ urgency }) {
  const config = {
    critical: { color: 'var(--red)',   bg: 'var(--red-light)',   label: 'Critical' },
    high:     { color: 'var(--amber)', bg: 'var(--amber-light)', label: 'High' },
    normal:   { color: 'var(--green)', bg: 'var(--green-light)', label: 'Normal' },
  };
  const c = config[urgency] || config.normal;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      fontSize: '11.5px', fontWeight: 600, padding: '3px 9px',
      borderRadius: '20px', background: c.bg, color: c.color,
    }}>
      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: c.color, flexShrink: 0 }} />
      {c.label}
    </span>
  );
}

export const COMPONENT_LABELS = {
  whole_blood: 'Whole Blood',
  platelets: 'Platelets',
  plasma: 'Plasma',
  cryoprecipitate: 'Cryoprecipitate',
};

export function ComponentBadge({ component }) {
  if (!component || component === 'whole_blood') return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: '11px', fontWeight: 700, padding: '3px 8px',
      borderRadius: '20px', background: 'var(--gray-100)', color: 'var(--gray-600)',
    }}>
      {COMPONENT_LABELS[component] || component}
    </span>
  );
}

export function BloodTypeBadge({ type }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: '44px', fontSize: '13px', fontWeight: 800,
      padding: '4px 10px', borderRadius: '8px',
      background: 'var(--red-light)', color: 'var(--red-dark)',
      letterSpacing: '0.01em',
    }}>
      {type}
    </span>
  );
}

/* ─── useIsMobile hook ─── */
export function useIsMobile() {
  const [mobile, setMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return mobile;
}

/* ─── Reveal: fades + slides an element up the first time it scrolls into view ─── */
export function Reveal({ children, delay = 0, style: extra, as: Tag = 'div' }) {
  const ref = React.useRef(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Already in the initial viewport (e.g. above-the-fold hero) — show immediately.
    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`bb-reveal${visible ? ' bb-reveal-visible' : ''}`}
      style={{ transitionDelay: `${delay}ms`, ...extra }}
    >
      {children}
    </Tag>
  );
}

/* ─── Counter: animates a number counting up to its target value ─── */
export function Counter({ value, duration = 1400, prefix = '', suffix = '', decimals = 0 }) {
  const ref = React.useRef(null);
  const [display, setDisplay] = React.useState(0);
  const started = React.useRef(false);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const run = () => {
      if (started.current) return;
      started.current = true;
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // ease-out-cubic
        setDisplay(value * eased);
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) { run(); return; }

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { run(); observer.disconnect(); } },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref}>
      {prefix}{display.toLocaleString(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}{suffix}
    </span>
  );
}

/* ─── Skeleton: shimmering placeholder block for loading states ─── */
export function Skeleton({ width = '100%', height = '14px', radius, style: extra }) {
  return (
    <div
      className="bb-skeleton"
      style={{ width, height, borderRadius: radius || 'var(--radius-sm)', ...extra }}
    />
  );
}

/* ─── MapPreview: pinned venue map + directions handoff ───
   Renders nothing when the place has no coordinates, so callers can drop it
   in unconditionally — camps created before location capture, or with an
   address that geocoding couldn't resolve, simply show no map. */
export function MapPreview({ place, height = '180px', label = 'Get directions', style: extra }) {
  const src = embedUrl(place);
  const directions = directionsUrl(place);
  if (!src) return null;

  return (
    <div style={{ ...extra }}>
      <iframe
        title={`Map of ${place.venue_name || 'the venue'}`}
        src={src}
        loading="lazy"
        style={{
          width: '100%', height, border: '1px solid var(--gray-200)',
          borderRadius: 'var(--radius)', display: 'block',
        }}
      />
      {directions && (
        <a
          href={directions}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            marginTop: '8px', fontSize: '12.5px', fontWeight: 600, color: 'var(--red)',
          }}
        >
          <Navigation size={13} /> {label}
        </a>
      )}
    </div>
  );
}
